import { type Object3D } from "three";
import type { GLTF } from "three/examples/jsm/Addons.js";
import DiagnosticsStore from "@/features/diagnostics/store/diagnostics-store.ts";
import { getMaterialByColourVariant, getMeshColourVariant } from "@/features/model-renderer/utils/gltf.ts";
import type { PartNo } from "@/features/sub-families/types";
import Logger from "@/lib/logger.ts";
import type { Nullable } from "@/types";
import ModelMap from "../lib/ModelMap.ts";
import { LINKED_PROPERTIES, PART } from "../resources/constants.ts";
import { isMesh } from "./types.ts";

export function toggleVisibility(gltf: Nullable<GLTF>, property: string, value: string) {
    const nodeNames = getNodeNamesByProperty(property);

    gltf?.scene?.traverse((child) => {
        if (!nodeNames.has(child.name)) return;

        setVisibility(child, property, value);
        toggleLinkedProperties(child);
    });
}

export async function toggleMaterial(gltf: Nullable<GLTF>, property: string, colour: string, partNo: PartNo) {
    const nodeNames = getNodeNamesByProperty(property);

    Logger.groupCollapsed("Parts and materials");
    DiagnosticsStore.collectEventLog("info", `[Model] Applying colour ${colour} to part ${partNo}`);

    gltf?.scene?.traverse(async (child) => {
        const isAllowedNode = nodeNames.has(child.name);
        const isAllowedPropertyMesh = (child.userData[property] as PartNo) === partNo;

        if (!(isMesh(child) && isAllowedNode && isAllowedPropertyMesh)) {
            return;
        }

        Logger.newLine();
        Logger.debug(`Rendering part ${partNo} with colour ${colour}`);

        const colourVariant = getMeshColourVariant(child);

        Logger.debug(`Colour variant ${colourVariant ?? "No colour variant found!"}`);

        if (!colourVariant) {
            Logger.warn("Mesh has no colour variant attached!", child);
            DiagnosticsStore.collectEventLog("warning", `No mesh found for colour variant ${colourVariant}!`);
            return;
        }

        const material = await getMaterialByColourVariant(gltf, colour, colourVariant);
        if (!material) {
            const message = `No material found for color ${colour} and variant ${colourVariant}`;

            Logger.warn(message);
            DiagnosticsStore.collectEventLog("warning", `[Model] ${message}}`);

            return;
        }

        child.material = material;
    });

    Logger.groupEnd();
}

export async function togglePartMaterial(gltf: Nullable<GLTF>, colour: string, partNo: PartNo) {
    await toggleMaterial(gltf, PART, colour, partNo);
}

function toggleLinkedProperties(child: Object3D) {
    const linkedProperty = child.userData[LINKED_PROPERTIES] as string | undefined;
    if (!linkedProperty) return;

    setVisibility(child, linkedProperty, "-1"); // hidden value?
}

function setVisibility(child: Object3D, property: string, value: string) {
    const isVisible = child.userData[property] === value;

    DiagnosticsStore.collectEventLog(
        "info",
        `[Model] Toggling visibility for ${child.name} (${isVisible ? "visible" : "invisible"})`,
    );

    child.visible = isVisible;
}

function getNodeNamesByProperty(property: string) {
    return ModelMap.getInstance().getNodeNamesByProperty(property);
}
